import type { ColumnState } from '@ag-grid-community/core';
import { Inject, Injectable, type OnDestroy, Optional } from '@angular/core';
// Import interfaces from external library
import type {
  HeadlessSelectedFilterField,
  SelectedFilterField,
} from '@cdw-ae/filter-bar-interfaces';
import { ComponentStore } from '@ngrx/component-store';
import { createEntityAdapter } from '@ngrx/entity';
import { difference } from 'lodash';
import {
  filter,
  lastValueFrom,
  map,
  type Observable,
  of,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { CDW_SAVE_COMPONENT_STATE_SERVICE_TOKEN } from '../config/cdw-save-component-state-service-token.constant';
import type {
  ICdwSaveComponentStateService,
  SearchGridDataSource,
} from '../interfaces/data-source.interface';
import type { CdwSearchGridComponentState } from './cdw-search-grid-component.interface.store';

function getCdwSearchGridAdapter<Tdata extends { id: string | number }>() {
  return createEntityAdapter<Tdata>();
}
export function initialComponentState<
  Tdata extends { id: string | number },
>(): CdwSearchGridComponentState<Tdata> {
  return getCdwSearchGridAdapter<Tdata>().getInitialState({
    dataLoaded: false,
    stateLoaded: false,
    selectedFilterFields: [],
    currentPage: 0,
    initialPageLoad: true,
    pageSize: 50,
    selectedIds: [],
    idsVisibleOnPage: [],
    multiSelectCheckboxChecked: false,
    multiSelectCheckboxIndeterminate: false,
    searchItemTotal: -1,
    // searchLoaded: false,
    selectAll: false,
    promptSelectAll: false,
    headlessFilterFields: [],
    filterBySelected: false,
    searchGridDataSource: 'sql',
    columnState: [],
    sortOrder: [],
  });
}

@Injectable()
export class CdwSearchGridComponentStore<Tdata extends { id: string | number }>
  extends ComponentStore<CdwSearchGridComponentState<Tdata>>
  implements OnDestroy
{
  // #region selectors
  public readonly selectDataLoaded$: Observable<boolean> = this.select(
    (state) => state.dataLoaded
  );

  public readonly initialPageLoad$: Observable<boolean> = this.select(
    (state) => state.initialPageLoad
  );

  public readonly componentId$: Observable<string | undefined> = this.select(
    (state) => state.componentId
  );

  public readonly headlessFilterFields$: Observable<
    HeadlessSelectedFilterField[]
  > = this.select(
    (state) => state.headlessFilterFields as HeadlessSelectedFilterField[]
  );

  public readonly searchItemTotal$: Observable<number> = this.select(
    (state) => state.searchItemTotal
  );

  public readonly currentPage$: Observable<number> = this.select(
    (state) => state.currentPage
  );

  public readonly stateLoaded$: Observable<boolean> = this.select(
    (state) => state.stateLoaded
  );

  public readonly nextPageToken$: Observable<string | undefined> = this.select(
    (state) => state.nextPageToken
  );

  public readonly totalItemsLoaded$: Observable<number> = this.select(
    (state) =>
      getCdwSearchGridAdapter<Tdata>().getSelectors().selectAll(state).length ??
      0
  );

  public readonly selectSelectAll$: Observable<Tdata[]> = this.select((state) =>
    getCdwSearchGridAdapter<Tdata>().getSelectors().selectAll(state)
  );

  public readonly pageSize$: Observable<number> = this.select(
    (state) => state.pageSize
  );

  public readonly entitiesExceedPageSize$: Observable<boolean> = this.select(
    (state) => state.searchItemTotal > state.pageSize
  );

  public readonly selectAll$: Observable<boolean> = this.select(
    (state) => state.selectAll
  );

  public readonly selectedFilterFields$: Observable<SelectedFilterField[]> =
    this.select((state) => state.selectedFilterFields);

  public readonly multiSelectCheckboxChecked$: Observable<boolean> =
    this.select((state) => state.multiSelectCheckboxChecked);

  public readonly multiSelectCheckboxIndeterminate$: Observable<boolean> =
    this.select((state) => state.multiSelectCheckboxIndeterminate);

  public readonly promptSelectAll$: Observable<boolean> = this.select(
    (state) => state.promptSelectAll
  );

  public readonly filterBySelected$: Observable<boolean> = this.select(
    (state) => state.filterBySelected
  );

  public readonly idsSelectedOnPage$: Observable<number> = this.select(
    (state) =>
      state.selectedIds.filter((selectedId: string | number): boolean =>
        state.idsVisibleOnPage.includes(selectedId)
      ).length
  );

  public readonly entireState$: Observable<CdwSearchGridComponentState<Tdata>> =
    this.select((state) => state);

  public readonly selectedIdCount$: Observable<number> = this.select(
    (state) => state.selectedIds.length
  );

  public readonly selectedIds$: Observable<(string | number)[]> = this.select(
    (state) => state.selectedIds
  );

  public readonly columnState$: Observable<ColumnState[]> = this.select(
    (state) => state.columnState
  );

  public readonly sortOrder$: Observable<
    { name: string; direction?: 'ASC' | 'DESC' }[]
  > = this.select((state) => state.sortOrder);

  public readonly total$: Observable<number | null> = this.select((state) => {
    if (state.searchGridDataSource === 'sql') {
      return state.searchItemTotal;
    }
    if (state.nextPageToken) {
      return null;
    }
    return Object.keys(state.entities).length;
  });

  // #endregion

  // #region actions

  public readonly initSearchGrid = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      dataLoaded: false,
      error: null,
      stateLoaded: true,
    })
  );

  // COMMON-930 Kate QA fix
  public readonly loadSearchGridSuccess = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>,
      props: { data: Tdata[] }
    ): CdwSearchGridComponentState<Tdata> =>
      getCdwSearchGridAdapter<Tdata>().addMany(props.data, {
        ...state,
        dataLoaded: true,
        selectedIds: state.selectedIds.filter(
          (selectedId: string | number): boolean =>
            props.data.filter((item: Tdata) => item.id === selectedId).length >
              0 || Object.keys(state.entities).includes(selectedId.toString())
        ),
      })
  );

  public readonly setComponentId = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>,
      props: { componentId: string }
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      componentId: props.componentId,
    })
  );

  public readonly setSearchGridDataSource = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>,
      props: { searchGridDataSource: SearchGridDataSource }
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      searchGridDataSource: props.searchGridDataSource,
    })
  );

  public readonly setCurrentPage = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>,
      props: { currentPage: number }
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      currentPage: props.currentPage,
    })
  );

  public readonly setInitialPageLoad = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>,
      props: { initialPageLoad: boolean }
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      initialPageLoad: props.initialPageLoad,
    })
  );

  public readonly setSearchItemTotal = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>,
      props: { searchItemTotal: number }
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      searchItemTotal: props.searchItemTotal,
    })
  );

  public readonly setNextPageToken = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>,
      props: { nextPageToken: string | undefined }
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      nextPageToken: props.nextPageToken,
    })
  );

  public readonly setSelectedFilterFields = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>,
      props: { selectedFilterFields: SelectedFilterField[] }
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      selectedFilterFields: props.selectedFilterFields,
    })
  );

  public readonly turnOffFilterBySelected = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      filterBySelected: false,
    })
  );

  public readonly turnOnFilterBySelected = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      filterBySelected: true,
    })
  );

  public readonly turnOffPromptSelectAll = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      promptSelectAll: false,
    })
  );

  public readonly turnOnPromptSelectAll = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      promptSelectAll: true,
    })
  );

  public readonly turnOffSelectAll = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      selectAll: false,
    })
  );

  public readonly turnOnSelectAll = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      selectAll: true,
      selectedIds: [...state.ids] as string[],
    })
  );

  /**
   * Turn on selectAll mode with a complete list of all IDs
   * Use this when you have fetched all IDs from the backend
   */
  public readonly turnOnSelectAllWithIds = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>,
      props: { allIds: (string | number)[] }
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      selectAll: true,
      selectedIds: props.allIds,
    })
  );

  public readonly setPageSize = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>,
      props: { pageSize: number }
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      pageSize: props.pageSize,
    })
  );

  public readonly clearSearch = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>
    ): CdwSearchGridComponentState<Tdata> =>
      getCdwSearchGridAdapter().removeAll({
        ...state,
        loaded: false,
        headlessFilterFields: [],
      })
  );

  public readonly clearSelectedIds = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      selectedIds: [],
    })
  );

  public readonly setSelectedIds = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>,
      props: { ids: (string | number)[] }
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      selectedIds: [...new Set([...state.selectedIds, ...props.ids])],
    })
  );

  public readonly removeSelectedIds = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>,
      props: { ids: (string | number)[] }
    ): CdwSearchGridComponentState<Tdata> => {
      // Convert all IDs to strings for consistent comparison
      const idsToRemove = props.ids.map((id) => id.toString());
      return {
        ...state,
        selectedIds: state.selectedIds.filter(
          (id: string | number) => !idsToRemove.includes(id.toString())
        ),
      };
    }
  );

  public readonly updateIdsVisibleOnPage = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>,
      props: { ids: (string | number)[] }
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      idsVisibleOnPage: props.ids,
    })
  );

  public readonly setHeadlessFilterFields = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>,
      props: { filterBySelectedColumnName: string }
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      headlessFilterFields: state.selectedIds.map((id: string | number) => ({
        selectedMatcher: { name: 'oneOf' },
        field: { name: props.filterBySelectedColumnName },
        matchValue: id,
      })),
    })
  );

  public readonly clearHeadlessFilterFields = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      headlessFilterFields: [],
    })
  );

  public readonly clearNextPageToken = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      nextPageToken: undefined,
    })
  );

  public readonly toggleSelectedId = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>,
      props: { id: string | number }
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      selectedIds: state.selectedIds.includes(props.id)
        ? state.selectedIds.filter(
            (selectedId: string | number): boolean => selectedId !== props.id
          )
        : [...new Set([...state.selectedIds, props.id])],
    })
  );

  public readonly apiSearchFilterBySelected = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>
    ): CdwSearchGridComponentState<Tdata> => {
      const removeIds = (
        getCdwSearchGridAdapter<Tdata>()
          .getSelectors()
          .selectIds(state) as string[]
      ).filter((id: string) => !state.selectedIds.includes(id));
      return getCdwSearchGridAdapter<Tdata>().removeMany(removeIds, {
        ...state,
        searchItemTotal: difference(Object.keys(state.entities), removeIds)
          .length,
      });
    }
  );

  public readonly setColumnState = this.effect(
    (props$: Observable<{ columnState: ColumnState[] }>) =>
      props$.pipe(
        tap((props) => {
          this.patchState({ columnState: props.columnState });
          this.saveComponentState();
        })
      )
  );

  public readonly setSortOrder = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>,
      props: { sortOrder: { name: string; direction?: 'ASC' | 'DESC' }[] }
    ): CdwSearchGridComponentState<Tdata> => ({
      ...state,
      sortOrder: [...new Set(props.sortOrder)],
    })
  );

  /**
   * `savedState` is `Partial` because it originates from persistence, which
   * may hold a blob written by an older schema (or by a different grid) and
   * so cannot be assumed to carry every key. The spread over `state` below
   * already supplies defaults for anything absent.
   */
  public readonly loadSavedState = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>,
      props: { savedState: Partial<CdwSearchGridComponentState<Tdata>> }
    ): CdwSearchGridComponentState<Tdata> => {
      // Exclude selectedFilterFields from saved state - filters should only come from URL/props
      const {
        selectedFilterFields: _selectedFilterFields,
        ...savedStateWithoutFilters
      } = props.savedState;
      return {
        ...state,
        ...savedStateWithoutFilters,
      };
    }
  );

  public readonly updateMultiSelectCheckboxState = this.updater(
    (
      state: CdwSearchGridComponentState<Tdata>
    ): CdwSearchGridComponentState<Tdata> => {
      // Convert visible IDs to strings for consistent comparison
      const visibleIdsAsStrings = state.idsVisibleOnPage.map((id) =>
        id.toString()
      );

      if (
        state.selectedIds.filter((selectedId: string | number): boolean =>
          visibleIdsAsStrings.includes(selectedId.toString())
        ).length === state.idsVisibleOnPage.length &&
        state.idsVisibleOnPage.length > 0
      ) {
        return {
          ...state,
          multiSelectCheckboxChecked: true,
          multiSelectCheckboxIndeterminate: false,
        };
      }
      if (
        state.selectedIds.filter((selectedId: string | number): boolean =>
          visibleIdsAsStrings.includes(selectedId.toString())
        ).length > 0
      ) {
        return {
          ...state,
          multiSelectCheckboxChecked: false,
          multiSelectCheckboxIndeterminate: true,
        };
      }
      return {
        ...state,
        multiSelectCheckboxChecked: false,
        multiSelectCheckboxIndeterminate: false,
      };
    }
  );

  // #endregion

  // #region effects
  public readonly saveComponentState = this.effect((trigger$) =>
    trigger$.pipe(
      withLatestFrom(this.entireState$),
      switchMap(([, entireState]) => {
        if (entireState.componentId && this.cdwSaveComponentStateService) {
          return of(
            this.cdwSaveComponentStateService.saveComponentState(
              entireState.componentId,
              entireState
            )
          );
        }
        return of(null);
      })
    )
  );

  public readonly retrieveComponentState = this.effect(() => {
    if (this.cdwSaveComponentStateService) {
      return this.componentId$.pipe(
        filter(CdwSearchGridComponentStore.isString),
        take(1),
        map((componentId) =>
          this.cdwSaveComponentStateService.retrieveComponentState(componentId)
        ),
        switchMap((savedState) => {
          // `retrieveComponentState` returns `unknown` — the blob comes from
          // localStorage and can be anything. Narrow to "a non-null object"
          // before spreading; a primitive or malformed value degrades to "no
          // restore" rather than corrupting store state. Individual fields
          // are still unvalidated (pre-existing behaviour), which is why the
          // cast is scoped to this one boundary rather than living in the
          // service contract.
          if (savedState === null || typeof savedState !== 'object') {
            return of();
          }
          return of(
            this.loadSavedState({
              savedState: {
                ...(savedState as Partial<CdwSearchGridComponentState<Tdata>>),
                initialPageLoad: true,
              },
            })
          );
        })
      );
    }
    return of();
  });

  // #endregion

  constructor(
    @Optional()
    @Inject(CDW_SAVE_COMPONENT_STATE_SERVICE_TOKEN)
    private cdwSaveComponentStateService: ICdwSaveComponentStateService<
      CdwSearchGridComponentState<Tdata>
    >
  ) {
    super(initialComponentState<Tdata>());
  }

  private static isString(value: unknown): value is string {
    return typeof value === 'string';
  }

  public override async ngOnDestroy() {
    await lastValueFrom(of(this.saveComponentState()));
    super.ngOnDestroy();
  }

  // #region factory selectors
  public selectPartialSearch(props: {
    startRow: number;
    endRow: number;
  }): Observable<Tdata[]> {
    return this.select((state) =>
      getCdwSearchGridAdapter<Tdata>()
        .getSelectors()
        .selectAll(state)
        .slice(props.startRow, props.endRow)
    );
  }

  public isIdSelected(props: { id: string | number }): Observable<boolean> {
    return this.select((state) => state.selectedIds.includes(props.id));
  }
  // #endregion
}
